const EPS = 1e-9;

/**
 * Pick a "nice" tick step size for a given numeric span.
 *
 * The returned step is constrained to the classic 1/2/5/10 series scaled by a power of ten.
 * This makes axis labels easier to read than arbitrary fractional steps.
 *
 * @param span - Absolute domain span (max - min). Must be > 0.
 * @param targetTicks - Desired number of ticks. Defaults to 5.
 * @returns A positive step size. Returns 1 for invalid inputs.
 */
export const niceStep = (span: number, targetTicks = 5): number => {
  if (!Number.isFinite(span) || span <= 0) {
    return 1;
  }
  const rough = span / Math.max(1, targetTicks - 1);
  const exponent = Math.floor(Math.log10(rough));
  const base = rough / 10 ** exponent;

  let niceBase: number;
  if (base <= 1) {
    niceBase = 1;
  } else if (base <= 2) {
    niceBase = 2;
  } else if (base <= 5) {
    niceBase = 5;
  } else {
    niceBase = 10;
  }

  return niceBase * 10 ** exponent;
};

/**
 * Round a value to a stable decimal precision derived from the given step size.
 *
 * This exists to minimize floating-point accumulation issues when generating tick sequences.
 */
const roundToStep = (value: number, step: number) => {
  if (step === 0) {
    return value;
  }
  const decimals = Math.max(0, -Math.floor(Math.log10(step)) + 1);
  return Number(value.toFixed(decimals + 1));
};

/**
 * Generate a sorted set of ticks for a numeric domain.
 *
 * Ensures the returned array always includes the domain endpoints (`min` and `max`) and
 * is deduplicated/rounded to keep floating-point noise from producing near-identical values.
 *
 * @param min - Domain minimum.
 * @param max - Domain maximum.
 * @param targetTicks - Desired tick count (including endpoints). Defaults to 5.
 * @returns An ascending list of tick values. Returns [] for non-finite inputs.
 */
export const generateTicks = (min: number, max: number, targetTicks = 5): number[] => {
  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return [];
  }
  if (min === max) {
    return [min];
  }

  const [lo, hi] = min < max ? [min, max] : [max, min];
  const span = hi - lo;
  const step = niceStep(span, targetTicks);
  const ticks: number[] = [];

  const start = Math.ceil(lo / step) * step;
  for (let t = start; t <= hi + EPS; t += step) {
    ticks.push(roundToStep(t, step));
  }

  if (ticks.length === 0) {
    ticks.push(lo, hi);
  } else {
    if (Math.abs(lo - ticks[0]) > EPS) {
      ticks.unshift(lo);
    }
    if (Math.abs(hi - ticks[ticks.length - 1]) > EPS) {
      ticks.push(hi);
    }
  }

  const deduped = Array.from(new Set(ticks.map((t) => roundToStep(t, step))));
  return deduped.sort((a, b) => a - b);
};

/**
 * Format a numeric tick label based on the current step size.
 *
 * Uses a small number of decimals derived from the step to keep labels readable
 * (0 decimals for steps >= 1, 1 decimal for 0.1..1, and 2 decimals for < 0.1).
 *
 * @param value - Tick value.
 * @param step - Tick step size (used to infer decimals).
 * @returns A human-readable string, or "" for non-finite values.
 */
export const formatTick = (value: number, step: number) => {
  if (!Number.isFinite(value)) {
    return "";
  }

  const absStep = Math.abs(step);
  let decimals = 0;
  if (absStep < 1 && absStep >= 0.1) {
    decimals = 1;
  } else if (absStep < 0.1) {
    decimals = 2;
  }

  const rounded = Number(value.toFixed(decimals));
  return rounded.toString();
};
